import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Progress, Spin, message, Avatar, Divider, Collapse } from 'antd';
import { SmileOutlined, FrownOutlined, MehOutlined, RobotOutlined, UserOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import ApiService from '../service/api-service';
import type { EchoReport as EchoReportType, Transcript, TranscriptRole } from '../types/echo-report';

const { Title, Text } = Typography;

interface MetricCardProps {
  title: string;
  value: string | number;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value }) => (
  <div className="bg-white rounded-lg p-6 border-2 border-gray-100 h-full flex flex-col items-center justify-center text-center">
    <Text className="text-gray-600 mb-3 font-medium">{title}</Text>
    <Text className="text-3xl font-semibold text-blue-600">{value}</Text>
  </div>
);

interface TranscriptMessageProps {
  transcript: Transcript;
}

const TranscriptMessage: React.FC<TranscriptMessageProps> = ({ transcript }) => {
  const isAssistant = transcript.role === 'ASSISTANT';
  
  return (
    <div className={`flex gap-4 mb-4 ${isAssistant ? '' : 'flex-row-reverse'}`}>
      <Avatar 
        icon={isAssistant ? <RobotOutlined /> : <UserOutlined />}
        className={isAssistant ? 'bg-blue-500' : 'bg-green-500'}
      />
      <div 
        className={`
          max-w-[70%] rounded-lg p-3 
          ${isAssistant ? 'bg-blue-50' : 'bg-green-50'}
        `}
      >
        <Text>{transcript.transcript}</Text>
        <Text className="block text-xs text-gray-500 mt-1">
          {dayjs(transcript.createdAt).format('HH:mm:ss')}
        </Text>
      </div>
    </div>
  );
};

const EchoReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<EchoReportType | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        if (!id) return;
        const response = await ApiService.getEchoReport(id);
        if (response.success) {
          setReport(response.data);
        } else {
          message.error(response.message);
        }
      } catch (error) {
        message.error('Failed to fetch report');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-6">
        <Card className="shadow-lg rounded-xl">
          <div className="text-center py-8">
            <Text className="text-lg text-gray-600">Report not found</Text>
          </div>
        </Card>
      </div>
    );
  }

  const getEmotionIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'POSITIVE':
        return <SmileOutlined className="text-4xl text-green-500" />;
      case 'NEGATIVE':
        return <FrownOutlined className="text-4xl text-red-500" />;
      default:
        return <MehOutlined className="text-4xl text-yellow-500" />;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Card className="shadow-lg rounded-xl">
        <Title level={2} className="text-center mb-8 font-semibold">Echo Analysis</Title>

        {/* Header Information */}
        <div className="mb-8 bg-gray-50 p-6 rounded-xl">
          <Row gutter={[24, 16]}>
            <Col span={8}>
              <Text className="text-gray-600 block mb-2 text-sm">User Name:</Text>
              <Text className="text-lg font-medium">{`${report.audience.firstName} ${report.audience.lastName}`}</Text>
            </Col>
            <Col span={8}>
              <Text className="text-gray-600 block mb-2 text-sm">Product Name:</Text>
              
              <Text className="text-lg font-medium">{report.product.name}</Text>
            </Col>
            <Col span={8} className="text-right">
              <Text className="text-gray-600 block mb-2 text-sm">Feedback Date:</Text>
              <Text 
                className="text-lg font-medium cursor-help" 
                title={dayjs(report.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              >
                {dayjs(report.createdAt).format('YYYY-MM-DD')}
              </Text>
            </Col>
          </Row>
        </div>

        {/* Quantitative Metrics */}
        <Card 
          title={<span className="text-lg font-semibold">Quantitative Metrics</span>}
          className="mb-8 shadow-sm"
          bodyStyle={{ padding: '24px' }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={8}>
              <MetricCard title="Satisfaction Score (1-5)" value={report.satisfactionScore} />
            </Col>
            <Col xs={24} sm={8}>
              <MetricCard title="Recommendation Score" value={report.recommendationScore} />
            </Col>
            <Col xs={24} sm={8}>
              <MetricCard 
                title="Avg. Response Time/Ques" 
                value={`${report.averageResponseTime.toFixed(2)} seconds`} 
              />
            </Col>
          </Row>
        </Card>

        {/* Sentiment Metrics */}
        <Card 
          title={<span className="text-lg font-semibold">Sentiment Metrics</span>}
          className="mb-8 shadow-sm"
          bodyStyle={{ padding: '24px' }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Card className="h-full border-2">
                <Title level={5} className="mb-6">Sentiment Score</Title>
                <div className="text-center">
                  <Text className="block mb-4 text-lg">{report.sentiment}</Text>
                  <div className="relative h-2 bg-gray-200 rounded-full mb-4">
                    <div 
                      className="absolute h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full"
                      style={{ width: '100%' }}
                    />
                    <div 
                      className="absolute w-4 h-4 bg-white border-2 border-blue-600 rounded-full top-1/2 transform -translate-y-1/2"
                      style={{ 
                        left: report.sentiment === 'POSITIVE' ? '90%' : 
                              report.sentiment === 'NEGATIVE' ? '10%' : '50%' 
                      }}
                    />
                  </div>
                  <div className="flex justify-between mt-2">
                    <Text className="text-red-500">Negative</Text>
                    <Text className="text-green-500">Positive</Text>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="h-full border-2">
                <Title level={5} className="mb-6">Emotion Tag</Title>
                <div className="flex justify-center">
                  {getEmotionIcon(report.sentiment)}
                </div>
              </Card>
            </Col>
          </Row>
        </Card>

        {/* Highlights & Issues */}
        <Card 
          title={<span className="text-lg font-semibold">Highlights & Issues</span>}
          className="mb-8 shadow-sm"
          bodyStyle={{ padding: '24px' }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Card className="h-full border-2">
                <Title level={5} className="mb-4">Customer Appreciations</Title>
                <div className="flex justify-center">
                  <Progress 
                    type="circle" 
                    percent={Math.round((report.positives.length / (report.positives.length + report.negatives.length || 1)) * 100)} 
                    strokeColor="#4CAF50" 
                  />
                </div>
                <div className="mt-6">
                  {report.positives.map(positive => (
                    <Text key={positive.id} className="block mb-2">• {positive.feedback}</Text>
                  ))}
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="h-full border-2">
                <Title level={5} className="mb-4">User Frustrations</Title>
                <div className="flex justify-center">
                  <Progress 
                    type="circle" 
                    percent={Math.round((report.negatives.length / (report.positives.length + report.negatives.length || 1)) * 100)} 
                    strokeColor="#f5222d" 
                    success={{ percent: 0 }}
                  />
                </div>
                <div className="mt-6">
                  {report.negatives.map(negative => (
                    <Text key={negative.id} className="block mb-2">• {negative.feedback}</Text>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>
        </Card>

        {/* Onboarding Information */}
        {report.onboardedBy !=='Unknown' && <Card 
        className="mb-8 shadow-sm"
        title={<span className="text-lg font-semibold">Onboarding Information</span>}
        >                    
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <Text className="text-gray-600 block mb-2">Onboarded by:</Text>
              <Text className="text-lg font-medium">{report.onboardedBy}</Text>
            </Col>
            <Col span={24}>
              <Text className="text-gray-600 block mb-2">Onboarding Experience:</Text>
              <Text className="text-lg">{report.onboardingExperience || '-'}</Text>
            </Col>
          </Row>
        </Card>}

        {/* Transcripts */}
        <Card 
          title={<span className="text-lg font-semibold">Transcripts of the feedback</span>}
          className="mb-8 shadow-sm"
        >
          {report.transcripts.length > 0 ? (
            <div className="max-h-[500px] overflow-y-auto p-4">
              {report.transcripts
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .map(transcript => (
                  <TranscriptMessage key={transcript.id} transcript={transcript} />
                ))
              }
            </div>
          ) : (
            <Text className="text-gray-600">No transcripts available</Text>
          )}
        </Card>

        {/* Per Question Videos */}
        <Card 
          title={<span className="text-lg font-semibold">Question Videos</span>}
          className="mb-8 shadow-sm"
        >
          {report.transcripts.length > 0 ? (
            <div className="p-4">
              <Collapse>
                {report.transcripts
                  .filter(transcript => transcript.role === 'USER' && transcript.readURL)
                  .map((transcript, index) => {
                    // Find the previous ASSISTANT transcript
                    const questionIndex = report.transcripts.findIndex(t => t.id === transcript.id);
                    const previousQuestion = questionIndex > 0 ? report.transcripts[questionIndex - 1] : null;
                    const questionText = previousQuestion?.role === 'ASSISTANT' ? previousQuestion.transcript : "Question " + index;
                    
                    return (
                      <Collapse.Panel
                        key={transcript.id}
                        header={
                          <div className="flex justify-between items-center pr-8 w-full text-wrap">
                            <span className="font-medium truncate max-w-[90%] text-wrap">Q: {questionText}</span>
                          </div>
                        }
                      >
                        <div className="space-y-4">
                          <div className="aspect-video bg-black rounded-lg overflow-hidden">
                            <video 
                              controls
                              className="w-full h-full"
                              src={transcript.readURL}
                            >
                              <source src={transcript.readURL} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        </div>
                      </Collapse.Panel>
                    );
                  })}
              </Collapse>
            </div>
          ) : (
            <Text className="text-gray-600">No question videos available</Text>
          )}
        </Card>

      </Card>
    </div>
  );
};

export default EchoReport; 